/**
 * PROJECT FINISH DEFAULTS (2026-10-11, docs/bim-plan/34-finish-studio.md §3, Matt: "set global
 * project settings like on broad finishes throughout (flooring is often shared throughout) and
 * have those auto apply with the ability to be overridden … If a room is wet room set it to
 * tile"). Stored once in the settings map (`finishDefaults`):
 *  - `floor`: the flooring throughout — every placed room's floor that nobody has painted;
 *  - `wetFloor`: wet rooms instead (bath, powder, shower, water closet, laundry — by the Concept
 *    room's Room Template, mirrored onto the room pin as `template`).
 * Resolution, per floor area: painted by hand (the Paint Bucket) → the room's default → nothing.
 * Rooms that never take flooring by default: garage, covered patio, mechanical, electrical,
 * attic. A room with no Room Template takes the flooring throughout.
 *
 * Read by geometry through the resolved floor (`finishDefaultRooms`, `finishDefaultMats`):
 * doc.ts hands each floor the rooms on its level with their kind, and slabFinish.ts lays the
 * defaults inside each room's region (walls → regions, the plan's own room derivation).
 */
import { maps, transact, type ModelDoc } from './doc'
import type { DoorTransition, ThresholdKind } from './types'

export interface FinishDefaults {
  floor?: string | null
  wetFloor?: string | null
  /** FINISH TRANSITIONS: where a room's floor change sits in its doorways, and the standard
   *  threshold for new transitions (absent = centered under the door / no threshold) */
  doorTransition?: DoorTransition | null
  threshold?: ThresholdKind | null
}

const KEY = 'finishDefaults'

/** Room Templates that are wet rooms (Matt, 2026-10-10: "go with your instinct") */
export const WET_ROOM_TEMPLATES = ['Full Bath', 'Primary Bath', 'Powder Bath', 'Single Restroom', 'Water Closet', 'Shower', 'Laundry']
/** Room Templates that never take flooring by default */
export const NO_DEFAULT_FLOOR_TEMPLATES = ['Garage', 'Covered Patio', 'Mechanical', 'Electrical Room', 'Attic']

const norm = (s: string) => s.trim().toLowerCase()
const WET = new Set(WET_ROOM_TEMPLATES.map(norm))
const NONE = new Set(NO_DEFAULT_FLOOR_TEMPLATES.map(norm))

export type RoomFloorKind = 'wet' | 'dry' | 'none'

/** what a room's floor defaults to, by its Room Template (absent = the flooring throughout) */
export function roomFloorKind(template: string | null | undefined): RoomFloorKind {
  if (!template) return 'dry'
  const t = norm(template)
  return WET.has(t) ? 'wet' : NONE.has(t) ? 'none' : 'dry'
}

export function isWetRoomTemplate(template: string | null | undefined): boolean {
  return roomFloorKind(template) === 'wet'
}

export function getFinishDefaults(model: ModelDoc): FinishDefaults {
  const v = maps(model).settings.get(KEY) as FinishDefaults | undefined
  const door = (d: unknown): DoorTransition => (d === 'face' || d === 'far' ? d : 'center')
  return v && typeof v === 'object'
    ? { floor: v.floor || null, wetFloor: v.wetFloor || null, doorTransition: door(v.doorTransition), threshold: v.threshold || 'none' }
    : { floor: null, wetFloor: null, doorTransition: 'center', threshold: 'none' }
}

/** Patch-merge the defaults (null clears one). One transact = one undo step. */
export function setFinishDefaults(model: ModelDoc, patch: Partial<FinishDefaults>): void {
  transact(model, () => {
    maps(model).settings.set(KEY, { ...getFinishDefaults(model), ...patch })
  })
}
